import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";

import ReactTable from "../../reusableComponents/ReactTable/ReactTable";
import LoaderOverlay from "../Loader";
import { baseUrl } from "../../utils/api/apis";
import { DatePicker, Button, Tag, message, Switch } from "antd";
import moment from "moment";
import "./IpLogReports.scss";

const { RangePicker } = DatePicker;

const IpLogReports = () => {
  const { idToken } = useSelector((state) => state.auth);

  const [reports, setReports] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [isBST, setIsBST] = useState(true); // true = BST (UTC+1), false = UTC

  const [dateRange, setDateRange] = useState(null);

  // Server-side pagination params
  const [params, setParams] = useState({
    page: 1,
    page_size: 10,
  });

  // Columns based on sample log data
  const columns = useMemo(
    () => [
      {
        Header: "Email",
        accessor: "email",
      },
      {
        Header: "IP Address",
        accessor: "ip",
      },
      {
        Header: "Region",
        accessor: "region", // e.g. country code like "PH"
      },
      {
        Header: "Browser ID",
        accessor: "browser_id",
        Cell: ({ value }) => value || "-",
      },
      {
        Header: "Device Type",
        accessor: "device_type",
        Cell: ({ value }) => value || "-",
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ value }) => {
          const color = value === "Allowed" ? "green" : "red";
          return <Tag color={color}>{value}</Tag>;
        },
      },
      {
        Header: "Created At",
        accessor: "created",
        Cell: ({ value }) => {
          if (!value) return "-";
          const m = moment.utc(value);
          const display = isBST ? m.utcOffset("+01:00") : m;
          return display.format("YYYY/MM/DD hh:mm:ss a");
        },
      },
    ],
    [isBST] // re-derive when timezone toggle changes
  );

  const fetchIpLogReports = async (currentParams = params) => {
    if (!idToken) return;
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      message.warning("Please select a date range before exporting.");
      return;
    }
    setLoading(true);
    try {
      const config = {
        headers: { Authorization: `Bearer ${idToken}` },
        params: {
          page: currentParams.page,
          page_size: currentParams.page_size,
        },
      };

      if (dateRange && dateRange[0] && dateRange[1]) {
        config.params.start_date = dateRange[0].format("YYYY-MM-DD");
        config.params.end_date = dateRange[1].format("YYYY-MM-DD");
      }

      const response = await axios.get(
        `${baseUrl}auth/admin/ip-log-report/`,
        config
      );

      let rows = [];
      let count = 0;

      if (Array.isArray(response.data)) {
        rows = response.data;
        count = response.data.length;
      } else if (Array.isArray(response.data.results)) {
        rows = response.data.results;
        count = response.data.count ?? response.data.results.length;
      } else if (Array.isArray(response.data.data)) {
        rows = response.data.data;
        count = response.data.total ?? response.data.data.length;
      } else {
        rows = [];
        count = 0;
      }

      setReports(rows);
      setTotalCount(count);
    } catch (error) {
      console.error("Error fetching IP log reports:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCsv = async () => {
    if (!idToken) return;

    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      message.warning("Please select a date range before exporting.");
      return;
    }

    const start_date = dateRange[0].format("YYYY-MM-DD");
    const end_date = dateRange[1].format("YYYY-MM-DD");

    setExportLoading(true);
    try {
      const response = await axios.get(`${baseUrl}auth/admin/ip-log-report/`, {
        headers: { Authorization: `Bearer ${idToken}` },
        params: {
          export: "csv",
          start_date,
          end_date,
        },
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type: "text/csv;charset=utf-8;",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `ip-log-report-${start_date}-to-${end_date}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting CSV IP logs:", error);
      message.error("Failed to export CSV.");
    } finally {
      setExportLoading(false);
    }
  };

  // Fetch whenever page or page_size or dateRange changes
  useEffect(() => {
    fetchIpLogReports(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.page, params.page_size, idToken, dateRange]);

  return (
    <div className="table_scroll_wrapper ip-log-reports">
      <div className="alltable">
        <div className="table_wrapper">
          {loading && <LoaderOverlay />}

          <div className="ip-log-reports__toolbar">
            <div className="ip-log-reports__controls">
              <span className="ip-log-reports__label">Date Range:</span>
              <RangePicker
                className="ip-log-reports__range-picker"
                value={dateRange}
                onChange={(val) => setDateRange(val)}
                allowClear
              />
              <Button
                className="ip-log-reports__export-button"
                type="primary"
                onClick={handleExportCsv}
                loading={exportLoading}
              >
                Export CSV
              </Button>
            </div>

            <div className="ip-log-reports__timezone-toggle">
              <span style={{ fontWeight: 500, fontSize: 13, opacity: 0.8 }}>UTC</span>
              <Switch
                checked={isBST}
                onChange={(checked) => setIsBST(checked)}
                size="small"
              />
              <span
                style={{
                  fontWeight: 600,
                  fontSize: 13,
                  color: isBST ? "#4ade80" : "inherit",
                  transition: "color 0.2s",
                }}
              >
                BST
              </span>
            </div>
          </div>

          <div className="alltable">
            <div className="ip-log-reports__table-shell">
              <ReactTable
                columns={columns}
                data={reports}
                serverSide={true}
                params={params}
                setParams={setParams}
                totalCount={totalCount}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IpLogReports;
